<template>
  <section class="FinancialAccountsView">
    <SectionHeader
      v-if="accounts.length > 0"
      title="Accounts"
      :subtitle="`${accounts.length} connected`"
    />

    <div class="FinancialAccountsView__groupList">
      <FinancialAccountGroup
        v-for="group in accountGroups"
        :key="group.title"
        :title="group.title"
        :accounts="group.accounts"
        @click:account="handleAccountClick"
      />
    </div>

    <FeatureCard
      body="Connect your checking accounts, credit cards, and 401Ks for Era to analyze your spending, provide balance monitoring, and personalize news alerts to your financial picture."
      color="Green"
      :iconName="accounts.length === 0 ? 'Bank' : undefined"
      :title="accounts.length === 0 ? 'Get started' : 'Add more accounts'"
    >
      <ConnectAccountsButtonView />
    </FeatureCard>
  </section>
</template>

<script lang="ts" setup>
import FeatureCard from "@cosine/components/FeatureCard.vue";
import FinancialAccountGroup from "@cosine/components/FinancialAccountGroup.vue";
import SectionHeader from "@cosine/components/SectionHeader.vue";
import useAppSpinner from "@cosine/composables/useAppSpinner";
import useRequest from "@cosine/composables/useRequest";
import { RouteName } from "@cosine/routes";
import useFinancialStore from "@cosine/stores/useFinancialStore";
import { AccountType, IEnrichedAccountModel } from "@cosine/types/api-models";
import ConnectAccountsButtonView from "@cosine/views/ConnectAccountsButtonView.vue";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const financialStore = useFinancialStore();
const {
  accounts,
  activeAccounts,
  inactiveAccounts,
} = storeToRefs(financialStore);
const {
  fetchAccounts,
} = financialStore;

const {
  handleRequest,
  isRequesting,
} = useRequest();
useAppSpinner(isRequesting);

onMounted(async () => {
  await handleRequest(fetchAccounts());
});

const activeCashAccounts = computed(() => {
  return activeAccounts.value.filter((account) => {
    return [
      AccountType.Checking,
      AccountType.Savings,
      AccountType.Cash,
      AccountType.Prepaid,
    ].includes(account.Type);
  });
});

const activeCreditCardAccounts = computed(() => {
  return activeAccounts.value.filter((account) => {
    return account.Type === AccountType.CreditCard;
  });
});

const activeInvestmentAccounts = computed(() => {
  return activeAccounts.value.filter((account) => {
    return [AccountType.Investment, AccountType.Property].includes(account.Type);
  });
});

const activeLoanAccounts = computed(() => {
  return activeAccounts.value.filter((account) => {
    return [
      AccountType.Loan,
      AccountType.AutoLoan,
      AccountType.StudentLoan,
      AccountType.LineOfCredit,
      AccountType.Mortgage,
      AccountType.CheckingLineOfCredit,
    ].includes(account.Type);
  });
});

const activeMiscAccounts = computed(() => {
  return activeAccounts.value.filter((account) => {
    return [AccountType.Any, AccountType.Insurance].includes(account.Type);
  });
});

const accountGroups = computed(() => {
  return [
    {
      title: "Cash",
      accounts: activeCashAccounts.value,
    },
    {
      title: "Credit cards",
      accounts: activeCreditCardAccounts.value,
    },
    {
      title: "Investments",
      accounts: activeInvestmentAccounts.value,
    },
    {
      title: "Loans",
      accounts: activeLoanAccounts.value,
    },
    ...(activeMiscAccounts.value.length > 0
      ? [
        {
          title: "Miscellaneous",
          accounts: activeMiscAccounts.value,
        },
      ]
      : []
    ),
    ...(inactiveAccounts.value.length > 0
      ? [
        {
          title: "Inactive accounts",
          accounts: inactiveAccounts.value,
        },
      ]
      : []
    ),
  ];
});

function handleAccountClick (account: IEnrichedAccountModel) {
  router.push({
    name: RouteName.accountsView,
    params: {
      id: account.EraAccountId,
    },
  });
}
</script>

<style lang="scss" scoped>
.FinancialAccountsView__groupList {
  display: grid;
  gap: 8px;
  padding: 8px;
}
</style>
